import type { Lang } from './countries';
import { getContent, SUPPORTED_LANGS } from '../i18n';
import { buildPath, type ModeKey } from '../router';

const SITE = 'https://geodle.org';

function ensure(selector: string, create: () => HTMLElement): HTMLElement {
  let el = document.head.querySelector(selector) as HTMLElement | null;
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

function metaName(name: string, content: string) {
  const el = ensure(`meta[name="${name}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute('name', name);
    return m;
  });
  el.setAttribute('content', content);
}

function metaProp(prop: string, content: string) {
  const el = ensure(`meta[property="${prop}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute('property', prop);
    return m;
  });
  el.setAttribute('content', content);
}

function linkRel(rel: string, href: string) {
  const el = ensure(`link[rel="${rel}"]:not([hreflang])`, () => {
    const l = document.createElement('link');
    l.setAttribute('rel', rel);
    return l;
  });
  el.setAttribute('href', href);
}

/** 路由变化时更新整套 <head>:title/description/canonical/og/twitter + hreflang + FAQ JSON-LD */
export function applySeo(lang: Lang, mode: ModeKey, endless = false): void {
  const c = getContent(lang);
  const m = c.meta[mode];
  const title = endless ? `${m.h1} — ${c.unlimited.titleSuffix} | GeoDle` : m.title;
  const desc = endless ? c.unlimited.desc : m.desc;
  const url = SITE + buildPath(lang, mode, endless);
  const ogImage = `${SITE}/og/${mode}.png`;

  document.title = title;
  document.documentElement.lang = lang;
  metaName('description', desc);
  linkRel('canonical', url);

  metaProp('og:title', title);
  metaProp('og:description', desc);
  metaProp('og:url', url);
  metaProp('og:image', ogImage);
  metaName('twitter:card', 'summary_large_image');
  metaName('twitter:title', title);
  metaName('twitter:description', desc);
  metaName('twitter:image', ogImage);

  // hreflang 互链 + x-default
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
  const alts: [string, Lang][] = [
    ...SUPPORTED_LANGS.map((l) => [l, l] as [string, Lang]),
    ['x-default', 'en'],
  ];
  for (const [hreflang, l] of alts) {
    const a = document.createElement('link');
    a.setAttribute('rel', 'alternate');
    a.setAttribute('hreflang', hreflang);
    a.setAttribute('href', SITE + buildPath(l, mode, endless));
    document.head.appendChild(a);
  }

  // FAQ 结构化数据(竞品多数没做好,是 SEO 超车点)
  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: c.content.faq.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
  const ld = ensure('script#faq-jsonld', () => {
    const s = document.createElement('script');
    s.id = 'faq-jsonld';
    s.setAttribute('type', 'application/ld+json');
    return s;
  });
  ld.textContent = JSON.stringify(faqLd);
}
